import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { color, r, radius } from '../../theme/tokens';
import { GUEST_CHECK_STATUS } from '../../hooks/useGuestInteractionCheck';

// "Your medications" on the stack page for a logged-out visitor (STACK-563
// mock, s8–s13a). Read-only, ruled 2026-10-08: the logged-out check is a
// taste of the feature, and editing here would let one stack be checked
// against medication after medication. The link sends the visitor to a new
// stack instead.
//
// The status line is the one place the check reports its outcome. Every
// refusal is a single couldn't-check notice that cannot be read as clean
// (STACK-528's rule: never claim a check that did not run) — ink and a
// neutral border, because amber and red mean interaction risk.

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const statusRow = (icon, title, detail) => (
  <Box
    role="status"
    sx={{ display: 'flex', gap: r(8), alignItems: 'flex-start', mt: r(12), fontSize: r(14), lineHeight: 1.5, color: color.textBody }}
  >
    <Box sx={{ flex: 'none', display: 'inline-flex', mt: r(2) }}>{icon}</Box>
    <Box>
      <Box component="b" sx={{ display: 'block', color: color.ink }}>{title}</Box>
      <Box component="small" sx={{ display: 'block', color: color.inkSoft, fontSize: r(13) }}>{detail}</Box>
    </Box>
  </Box>
);

export default function GuestMedicationsSummary({ medications, status, flagCount, checkedCount, onRetry }) {
  const n = medications.length;
  let line = null;

  if (status === GUEST_CHECK_STATUS.CHECKING) {
    line = statusRow(
      <CircularProgress size={r(16)} sx={{ color: color.ink }} aria-hidden />,
      `Checking ${plural(checkedCount, 'supplement', 'supplements')} against ${plural(n, 'medication', 'medications')}…`,
      "This can take up to 2 minutes when we haven't checked these before. You can read your stack meanwhile.",
    );
  } else if (status === GUEST_CHECK_STATUS.FLAGS) {
    line = statusRow(
      <InfoOutlinedIcon sx={{ fontSize: r(16), color: color.ink }} aria-hidden />,
      `${flagCount} of ${plural(checkedCount, 'supplement', 'supplements')} flagged with your medications.`,
      'Tap a risk badge on a supplement to see why.',
    );
  } else if (status === GUEST_CHECK_STATUS.CLEAN) {
    line = statusRow(
      <CheckRoundedIcon sx={{ fontSize: r(16), color: color.accent }} aria-hidden />,
      `No interactions flagged with your ${plural(n, 'medication', 'medications')}.`,
      `We checked all ${plural(checkedCount, 'supplement', 'supplements')}. A check can miss things, so review your stack with your pharmacist.`,
    );
  } else if (status === GUEST_CHECK_STATUS.ERROR) {
    line = (
      <Box
        role="alert"
        data-testid="guest-check-couldnt-check"
        sx={{
          mt: r(12),
          border: `${r(1)} solid ${color.inkMuted}`,
          backgroundColor: color.paper,
          borderRadius: radius.md,
          p: r(12),
          display: 'flex',
          gap: r(10),
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <InfoOutlinedIcon sx={{ flex: 'none', fontSize: r(16), color: color.ink, mt: r(2) }} aria-hidden />
        <Box sx={{ flex: `1 1 ${r(224)}`, fontSize: r(14), lineHeight: 1.5, color: color.textBody }}>
          <Box component="b" sx={{ display: 'block', color: color.ink }}>
            We couldn't check your medications against this stack.
          </Box>
          No interaction flags are shown because the check didn't run, not because there are none.
          Review this stack with your pharmacist or doctor before you start it.
        </Box>
        <ButtonBase
          onClick={onRetry}
          sx={{
            flex: 'none',
            minHeight: r(44),
            px: r(18),
            borderRadius: r(10),
            border: `${r(1)} solid ${color.line}`,
            backgroundColor: color.paper,
            color: color.evergreen,
            fontWeight: 600,
            fontSize: r(14),
          }}
        >
          Try again
        </ButtonBase>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-label="Your medications"
      data-testid="guest-medications-summary"
      sx={{
        mt: r(16),
        border: `${r(1)} solid ${color.line}`,
        borderRadius: radius.lg,
        backgroundColor: color.surface,
        px: r(16),
        py: r(12),
      }}
    >
      <Typography
        sx={{ fontSize: r(12), fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.inkSoft, mb: r(8) }}
      >
        Your medications
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: r(6) }}>
        {medications.map((m) => (
          <Box
            component="li"
            key={m.in_rxcui}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: r(6),
              maxWidth: '100%',
              height: r(32),
              pl: r(8),
              pr: r(10),
              border: `${r(1)} solid ${color.line}`,
              borderRadius: r(10),
              backgroundColor: color.paper,
              fontSize: r(13),
              fontWeight: 600,
              color: color.ink,
            }}
          >
            <MedicationOutlinedIcon sx={{ fontSize: r(16), color: color.evergreen, flex: 'none' }} aria-hidden />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {m.display_name}
            </Box>
          </Box>
        ))}
      </Box>
      {line}
      <Typography sx={{ mt: r(10), fontSize: r(13), color: color.inkSoft, lineHeight: 1.45 }}>
        These stay the same for this stack. To check different medications,{' '}
        <Box
          component={RouterLink}
          to="/create-stack"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{ color: color.linkHover, fontWeight: 600 }}
        >
          start a new stack
        </Box>{' '}
        and add them there.
      </Typography>
    </Box>
  );
}
